import { CSSProperties, memo, ReactElement, useMemo } from 'react';
import cc from 'classcat';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { createArray } from 'utils/helpers/create-fake-array';
import { orderOptions } from 'utils/sportsbook/order-correct-score-market';
import { TPopover } from 'interfaces/betslip';
import { EmptyOddGroup } from './EmptyOddGroup';
import { Odd } from './Odd';
import { EmptyOdd } from 'components/Odds/EmptyOdd';
// eslint-disable-next-line import/order
import './oddButtonStyles.less';

export const FULL_WIDTH_PERCENTAGE = 100;

const TWO_COLUMN_MARKET = [
  'CorrectScore',
  'SetCorrectScore',
  'GameCorrectScore'
];

export type TOddOptions = {
  value: string;
  coeficient: number;
  handicap?: string;
}[];

type TProps = {
  marketType?: string;
  value?: string[] | string;
  options?: TOddOptions;
  onChange?: Function;
  size?: 'default' | 'large';
  gameId?: number | string;
  marketColCount?: number;
  noBoosted?: boolean;
  whiteText?: boolean;
  showHandicaps?: boolean;
  hideHandicapName?: boolean;
  fullName?: boolean;
  popover?: TPopover;
  hidePopover?: () => void;
  isCorrectScoreOrdered?: boolean;
  isBlocked?: number;
  isLive?: boolean;
  emptyOddStyle?: boolean;
  style?: CSSProperties;
  oddTextColor?: string;
  colorVariables?: Record<string, string>;
  isUsaSinglePage?: boolean;
  oddFormat: number;
};

if (SpringConfigs.IS_RTL) {
  import('./rtl.less');
}

const MARKET_COL_COUNT_DEFAULT = 3;
export const Odds = memo((props: TProps): ReactElement => {
  const isCorrectScore = props.marketType?.includes('CorrectScore');

  const isTwoColumnMarket = TWO_COLUMN_MARKET.includes(props.marketType || '');

  const size = props.size || 'default';

  const marketColCount = isCorrectScore
    ? isTwoColumnMarket
      ? props.marketColCount
      : MARKET_COL_COUNT_DEFAULT
    : props.marketColCount;

  const options = useMemo(() => {
    let result: TOddOptions | undefined;

    if (isCorrectScore) {
      let correctScoreOptions: TOddOptions | undefined;

      if (!props.isCorrectScoreOrdered) {
        correctScoreOptions = props.options && orderOptions(props.options);
      } else {
        correctScoreOptions = props.options;
      }

      result = correctScoreOptions?.filter(option =>
        isTwoColumnMarket ? !!option : true
      );
    } else {
      result = props.options;
    }

    return result;
  }, [
    props.options,
    props.isCorrectScoreOrdered,
    isCorrectScore,
    isTwoColumnMarket
  ]);

  const additionalEmptyMarketsCount =
    Math.ceil((options?.length || 1) / (marketColCount || 1)) *
      (marketColCount || 1) -
    (options?.length || 1);

  return options?.length ? (
    <>
      {options.map((option, index) =>
        !option ? (
          <span
            key={index}
            {...(marketColCount && {
              style: {
                minWidth: `${
                  Math.floor(FULL_WIDTH_PERCENTAGE / marketColCount) - 2
                }%`
              }
            })}
            className={cc([
              'xOddButton',
              'xOddButton--disabled',
              {
                'xOddButton--large': size === 'large'
              }
            ])}
          >
            -
          </span>
        ) : (
          <Odd
            key={option.value}
            size={size}
            oddFormat={props.oddFormat}
            noBoosted={props.noBoosted}
            gameId={props.gameId || ''}
            fullName={props.fullName}
            isBlocked={props.isBlocked}
            isLive={props.isLive}
            isUsaSinglePage={props.isUsaSinglePage}
            parent={
              {
                ...props,
                marketColCount
              } as {
                value: string[];
                onChange: Function;
                marketColCount: number;
                popover: TPopover;
                hidePopover: () => void;
              }
            }
            {...option}
            style={props.style}
            oddTextColor={props.oddTextColor}
          />
        )
      )}
      {createArray(additionalEmptyMarketsCount).map(i => (
        <EmptyOdd key={i} emptyOddStyle />
      ))}
    </>
  ) : (
    <EmptyOddGroup colorVariables={props.colorVariables} size={size} />
  );
});
